import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import { enqueueSnackbar } from 'notistack';

import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import GppBadIcon from '@mui/icons-material/GppBad';
import SecurityIcon from '@mui/icons-material/Security';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';

import SearchBar from '../../components/mui/SearchBar';
import CustomButton from '../../components/mui/CustomButton';
import FraudModal from '../../components/FraudModal';
import { getUsersWithOrders, getUserOrdersById, downloadOrderInvoice } from '../../actions/ordersAuth';

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.04, duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  }),
};

const PanelCard = ({ children, sx = {} }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: '24px',
      border: '1px solid',
      borderColor: alpha('#11409B', 0.08),
      bgcolor: '#fff',
      boxShadow: '0 4px 32px rgba(15, 23, 42, 0.06)',
      overflow: 'hidden',
      ...sx,
    }}
  >
    {children}
  </Card>
);

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: '#fff',
    '& fieldset': { borderColor: alpha('#11409B', 0.2) },
    '&:hover fieldset': { borderColor: '#11409B' },
  },
};

const getStatusBadge = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'SUCCESS' || s === 'PAID') {
    return {
      label: 'Success',
      color: '#16a34a',
      bg: alpha('#16a34a', 0.12),
      border: alpha('#16a34a', 0.25),
    };
  }
  if (s === 'PENDING' || s === 'PENDING_PAYMENT') {
    return {
      label: 'Pending',
      color: '#d97706',
      bg: alpha('#d97706', 0.12),
      border: alpha('#d97706', 0.25),
    };
  }
  if (s === 'FAILED' || s === 'CANCELLED' || s === 'EXPIRED') {
    return {
      label: s === 'EXPIRED' ? 'Expired' : 'Failed',
      color: '#dc2626',
      bg: alpha('#dc2626', 0.12),
      border: alpha('#dc2626', 0.25),
    };
  }
  return {
    label: status ? String(status).replace(/_/g, ' ') : '—',
    color: '#64748b',
    bg: alpha('#64748b', 0.12),
    border: alpha('#64748b', 0.25),
  };
};

const formatAmount = (val) => {
  const n = Number(val);
  if (!Number.isFinite(n)) return '—';
  return `₹${n.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatPhone = (phone) => {
  if (!phone) return '—';
  const cleaned = String(phone).replace(/\s/g, '');
  if (cleaned.startsWith('+')) return cleaned;
  if (/^91\d{10}$/.test(cleaned)) return `+${cleaned}`;
  return `+91 ${cleaned}`;
};

export const UsersOrdersTab = () => {
  const dispatch = useDispatch();

  // State for Users with Orders pagination and filters
  const [loading, setLoading] = useState(true);
  const [usersData, setUsersData] = useState([]);
  const [page, setPage] = useState(0); // 0-based indexing as per API spec
  const [size, setSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [orderStatus, setOrderStatus] = useState('');
  const [userRole, setUserRole] = useState('');
  const [userActive, setUserActive] = useState('');

  // Expandable row state
  const [expandedUserIds, setExpandedUserIds] = useState(new Set());

  // Modal inspection state for single user orders
  const [selectedUser, setSelectedUser] = useState(null);
  const [userOrdersModalOpen, setUserOrdersModalOpen] = useState(false);
  const [userOrdersLoading, setUserOrdersLoading] = useState(false);
  const [fetchedUserOrders, setFetchedUserOrders] = useState(null);
  const [userOrdersSubTab, setUserOrdersSubTab] = useState(0);
  const [userOrdersSearch, setUserOrdersSearch] = useState('');

  // Fraud modal state
  const [fraudModalOpen, setFraudModalOpen] = useState(false);
  const [fraudTargetUser, setFraudTargetUser] = useState(null);

  const handleOpenFraudModal = (user) => {
    setFraudTargetUser(user);
    setFraudModalOpen(true);
  };

  // Fetch Users with Orders
  const fetchUsersWithOrders = useCallback(() => {
    setLoading(true);
    dispatch(
      getUsersWithOrders(
        {
          page,
          size,
          search: searchTerm || undefined,
          orderStatus: orderStatus || undefined,
          role: userRole || undefined,
          active:
            userActive === ''
              ? undefined
              : userActive === 'true'
              ? true
              : false,
        },
        (response) => {
          const body = response?.data ?? response;
          const data =
            typeof body?.data === 'object' && body?.data !== null && !Array.isArray(body.data)
              ? body.data
              : body;

          const content =
            data?.content ||
            data?.users ||
            (Array.isArray(data) ? data : []);

          const totalElems =
            data?.totalElements ??
            data?.totalCount ??
            (Array.isArray(content) ? content.length : 0);

          const totPages =
            data?.totalPages ??
            (Math.ceil(totalElems / size) || 1);

          setUsersData(Array.isArray(content) ? content : []);
          setTotalElements(totalElems);
          setTotalPages(totPages);
          setLoading(false);
        },
        (err) => {
          console.error('Failed to fetch users with orders:', err);
          setLoading(false);
        }
      )
    );
  }, [dispatch, page, size, searchTerm, orderStatus, userRole, userActive]);

  useEffect(() => {
    fetchUsersWithOrders();
  }, [fetchUsersWithOrders]);

  // Aggregate stats across current page items (and total elements)
  const stats = useMemo(() => {
    let successCount = 0;
    let pendingCount = 0;
    let failedCount = 0;
    let totalOrdersSum = 0;

    usersData.forEach((u) => {
      const counts = u?.counts || {};
      const total = u?.totalOrdersCount ?? counts.total ?? (u?.orders?.length || 0);
      const success = u?.successOrdersCount ?? counts.success ?? (u?.successOrders?.length || 0);
      const pending = u?.pendingOrdersCount ?? counts.pending ?? (u?.pendingOrders?.length || 0);
      const failed = u?.failedOrdersCount ?? counts.failed ?? (u?.failedOrders?.length || 0);

      totalOrdersSum += Number(total) || 0;
      successCount += Number(success) || 0;
      pendingCount += Number(pending) || 0;
      failedCount += Number(failed) || 0;
    });

    return {
      totalUsers: totalElements,
      totalOrdersSum,
      successCount,
      pendingCount,
      failedCount,
    };
  }, [usersData, totalElements]);

  // Handle Search Input
  const handleSearch = (term) => {
    setSearchTerm(term);
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setOrderStatus('');
    setUserRole('');
    setUserActive('');
    setPage(0);
  };

  const toggleExpandUser = (userId) => {
    setExpandedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  // Open User Orders Dialog
  const handleOpenUserOrdersModal = (user) => {
    setSelectedUser(user);
    setUserOrdersSubTab(0);
    setUserOrdersSearch('');
    setUserOrdersModalOpen(true);
    setFetchedUserOrders(null);

    // Call GET /api/users/{id}/orders to get full/fresh orders list
    if (user?.id) {
      setUserOrdersLoading(true);
      dispatch(
        getUserOrdersById(
          user.id,
          { page: 0, size: 100 },
          (res) => {
            const body = res?.data ?? res;
            const resData =
              typeof body?.data === 'object' && body?.data !== null
                ? body.data
                : body;
            const orders =
              resData?.orders ||
              resData?.content ||
              (Array.isArray(resData) ? resData : null);
            if (Array.isArray(orders)) {
              setFetchedUserOrders(orders);
            }
            setUserOrdersLoading(false);
          },
          () => {
            setUserOrdersLoading(false);
          }
        )
      );
    }
  };

  const handleCloseUserOrdersModal = () => {
    setUserOrdersModalOpen(false);
    setSelectedUser(null);
    setFetchedUserOrders(null);
  };

  // State & Handler for Downloading Order Invoice
  const [downloadingOrderId, setDownloadingOrderId] = useState(null);

  const parseBlobError = async (err) => {
    if (err?.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text();
        const json = JSON.parse(text);
        return json?.message || json?.error || 'Failed to download invoice';
      } catch {
        return 'Failed to download invoice';
      }
    }
    return err?.response?.data?.message || err?.message || 'Failed to download invoice';
  };

  const handleDownloadInvoice = (order) => {
    const orderId =
      order?.id ||
      order?.orderId ||
      order?._id ||
      order?.externalOrderIdVoucher;

    if (!orderId) {
      enqueueSnackbar('Order ID is missing for this order', { variant: 'error' });
      return;
    }

    setDownloadingOrderId(orderId);
    dispatch(
      downloadOrderInvoice(
        orderId,
        (response) => {
          try {
            const disposition =
              response?.headers?.['content-disposition'] ||
              response?.headers?.['Content-Disposition'];
            let filename = `invoice_${orderId}.pdf`;
            if (disposition) {
              const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
              if (match?.[1]) {
                filename = match[1].replace(/['"]/g, '').trim();
              }
            }
            const contentType =
              response?.headers?.['content-type'] ||
              response?.headers?.['Content-Type'] ||
              'application/pdf';
            const blob = new Blob([response.data], { type: contentType });
            saveAs(blob, filename);
            enqueueSnackbar('Invoice downloaded successfully', { variant: 'success' });
          } catch (e) {
            console.error('Failed to save invoice file:', e);
            enqueueSnackbar('Failed to save downloaded invoice', { variant: 'error' });
          } finally {
            setDownloadingOrderId(null);
          }
        },
        async (err) => {
          const errMsg = await parseBlobError(err);
          enqueueSnackbar(errMsg, { variant: 'error' });
          setDownloadingOrderId(null);
        }
      )
    );
  };

  // Export Users With Orders to Excel
  const exportToExcel = () => {
    const header = [
      'User ID',
      'Name',
      'Email',
      'Phone',
      'Username',
      'Role',
      'Active',
      'Blocked',
      'Total Orders',
      'Success Orders',
      'Pending Orders',
      'Failed Orders',
      'Created At',
      'Updated At',
    ];

    const dataToExport = usersData.map((u) => {
      const counts = u?.counts || {};
      const total = u?.totalOrdersCount ?? counts.total ?? (u?.orders?.length || 0);
      const success = u?.successOrdersCount ?? counts.success ?? (u?.successOrders?.length || 0);
      const pending = u?.pendingOrdersCount ?? counts.pending ?? (u?.pendingOrders?.length || 0);
      const failed = u?.failedOrdersCount ?? counts.failed ?? (u?.failedOrders?.length || 0);

      return [
        u?.id || '—',
        u?.name || '—',
        u?.email || '—',
        u?.phone ? formatPhone(u.phone) : '—',
        u?.username || '—',
        u?.role || '—',
        u?.isActive ? 'Yes' : 'No',
        u?.isBlocked ? 'Yes' : 'No',
        total,
        success,
        pending,
        failed,
        u?.createdAt ? dayjs(u.createdAt).format('YYYY-MM-DD HH:mm') : '—',
        u?.updatedAt ? dayjs(u.updatedAt).format('YYYY-MM-DD HH:mm') : '—',
      ];
    });

    dataToExport.unshift(header);
    const worksheet = XLSX.utils.aoa_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users_Orders");
    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    saveAs(
      new Blob([excelBuffer], { type: 'application/octet-stream' }),
      `Users_With_Orders_Page_${page + 1}.xlsx`
    );
  };

  // Extract orders list for dialog
  const activeUserOrdersList = useMemo(() => {
    if (!selectedUser) return [];

    let rawList = [];
    if (Array.isArray(fetchedUserOrders) && fetchedUserOrders.length > 0) {
      rawList = fetchedUserOrders;
    } else if (Array.isArray(selectedUser.orders) && selectedUser.orders.length > 0) {
      rawList = selectedUser.orders;
    } else {
      // Assemble from successOrders, pendingOrders, failedOrders
      const combined = [
        ...(selectedUser.successOrders || []),
        ...(selectedUser.pendingOrders || []),
        ...(selectedUser.failedOrders || []),
      ];
      rawList = combined;
    }

    // Filter by subtab: 0: All, 1: Success, 2: Pending, 3: Failed
    let filtered = rawList;
    if (userOrdersSubTab === 1) {
      filtered = rawList.filter((o) => {
        const s = String(o?.orderStatus || o?.paymentStatus || o?.status || o?.resultType || '').toUpperCase();
        return s === 'SUCCESS' || s === 'PAID';
      });
    } else if (userOrdersSubTab === 2) {
      filtered = rawList.filter((o) => {
        const s = String(o?.orderStatus || o?.paymentStatus || o?.status || o?.resultType || '').toUpperCase();
        return s === 'PENDING' || s === 'PENDING_PAYMENT';
      });
    } else if (userOrdersSubTab === 3) {
      filtered = rawList.filter((o) => {
        const s = String(o?.orderStatus || o?.paymentStatus || o?.status || o?.resultType || '').toUpperCase();
        return s === 'FAILED' || s === 'CANCELLED' || s === 'EXPIRED';
      });
    }

    // Search filter
    if (userOrdersSearch.trim()) {
      const q = userOrdersSearch.toLowerCase().trim();
      filtered = filtered.filter(
        (o) =>
          String(o?.id || '').toLowerCase().includes(q) ||
          String(o?.orderId || '').toLowerCase().includes(q) ||
          String(o?.transactionId || '').toLowerCase().includes(q) ||
          String(o?.productCode || '').toLowerCase().includes(q) ||
          String(o?.brandProductCode || '').toLowerCase().includes(q)
      );
    }

    return filtered;
  }, [selectedUser, fetchedUserOrders, userOrdersSubTab, userOrdersSearch]);

  const StatMini = ({ label, value, color, icon: Icon }) => (
    <Box
      sx={{
        p: 2.25,
        borderRadius: '18px',
        bgcolor: '#fff',
        border: `1px solid ${alpha(color, 0.18)}`,
        boxShadow: `0 4px 20px ${alpha(color, 0.08)}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        transition: 'transform 0.2s',
        '&:hover': { transform: 'translateY(-2px)' },
      }}
    >
      <Box>
        <Typography
          variant="caption"
          sx={{
            color: '#64748b',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontSize: '0.7rem',
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: '1.65rem',
            color: '#0f172a',
            lineHeight: 1.2,
            mt: 0.25,
          }}
        >
          {value}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: '12px',
          bgcolor: alpha(color, 0.1),
          color: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon sx={{ fontSize: 24 }} />
      </Box>
    </Box>
  );

  return (
    <Box>
      {/* Stat Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <motion.div custom={0} initial="hidden" animate="visible" variants={fadeUp}>
            <StatMini
              label="Users with Orders"
              value={stats.totalUsers.toLocaleString()}
              color="#2563eb"
              icon={PersonOutlineOutlinedIcon}
            />
          </motion.div>
        </Grid>
        <Grid item xs={6} sm={6} md={2.4}>
          <motion.div custom={1} initial="hidden" animate="visible" variants={fadeUp}>
            <StatMini
              label="Page Orders"
              value={stats.totalOrdersSum.toLocaleString()}
              color="#6366f1"
              icon={ShoppingBagOutlinedIcon}
            />
          </motion.div>
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <motion.div custom={2} initial="hidden" animate="visible" variants={fadeUp}>
            <StatMini
              label="Success Orders"
              value={stats.successCount.toLocaleString()}
              color="#16a34a"
              icon={CheckCircleOutlineIcon}
            />
          </motion.div>
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <motion.div custom={3} initial="hidden" animate="visible" variants={fadeUp}>
            <StatMini
              label="Pending Orders"
              value={stats.pendingCount.toLocaleString()}
              color="#d97706"
              icon={HourglassEmptyIcon}
            />
          </motion.div>
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <motion.div custom={4} initial="hidden" animate="visible" variants={fadeUp}>
            <StatMini
              label="Failed Orders"
              value={stats.failedCount.toLocaleString()}
              color="#dc2626"
              icon={ErrorOutlineIcon}
            />
          </motion.div>
        </Grid>
      </Grid>

      {/* Filter Card */}
      <motion.div custom={5} initial="hidden" animate="visible" variants={fadeUp}>
        <PanelCard sx={{ mb: 3, p: { xs: 2, md: 2.5 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={2}
            mb={2}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <FilterListOutlinedIcon sx={{ color: '#11409B' }} />
              <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>
                Filter Users & Orders
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <Tooltip title="Refresh data">
                <IconButton
                  onClick={fetchUsersWithOrders}
                  disabled={loading}
                  sx={{
                    bgcolor: alpha('#11409B', 0.08),
                    color: '#11409B',
                    '&:hover': { bgcolor: alpha('#11409B', 0.15) },
                  }}
                >
                  <RefreshOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <CustomButton
                variant="outlined"
                onClick={exportToExcel}
                disabled={loading || usersData.length === 0}
                startIcon={<FileDownloadOutlinedIcon />}
                sx={{
                  borderRadius: '12px',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderColor: alpha('#11409B', 0.25),
                  color: '#11409B',
                  '&:hover': {
                    borderColor: '#11409B',
                    bgcolor: alpha('#11409B', 0.04),
                  },
                }}
              >
                Export page
              </CustomButton>
            </Stack>
          </Stack>

          <Grid container spacing={2} alignItems="center">
            {/* Search */}
            <Grid item xs={12} md={4}>
              <SearchBar
                onSearch={handleSearch}
                searchTerm={searchTerm}
                height="2.75rem"
                placeholder="Search user name, email, phone, username..."
              />
            </Grid>

            {/* Order Status Filter */}
            <Grid item xs={12} sm={4} md={2.5}>
              <FormControl fullWidth size="small" sx={fieldSx}>
                <InputLabel>Order status</InputLabel>
                <Select
                  value={orderStatus}
                  label="Order status"
                  onChange={(e) => {
                    setOrderStatus(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All statuses</MenuItem>
                  <MenuItem value="SUCCESS">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#16a34a' }} />
                      <span>SUCCESS</span>
                    </Stack>
                  </MenuItem>
                  <MenuItem value="PENDING">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#d97706' }} />
                      <span>PENDING</span>
                    </Stack>
                  </MenuItem>
                  <MenuItem value="FAILED">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#dc2626' }} />
                      <span>FAILED</span>
                    </Stack>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* User Role Filter */}
            <Grid item xs={6} sm={4} md={2}>
              <FormControl fullWidth size="small" sx={fieldSx}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={userRole}
                  label="Role"
                  onChange={(e) => {
                    setUserRole(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All roles</MenuItem>
                  <MenuItem value="USER">USER</MenuItem>
                  <MenuItem value="CLIENT">CLIENT</MenuItem>
                  <MenuItem value="ADMIN">ADMIN</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* User Active Filter */}
            <Grid item xs={6} sm={4} md={2}>
              <FormControl fullWidth size="small" sx={fieldSx}>
                <InputLabel>User status</InputLabel>
                <Select
                  value={userActive}
                  label="User status"
                  onChange={(e) => {
                    setUserActive(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All users</MenuItem>
                  <MenuItem value="true">Active only</MenuItem>
                  <MenuItem value="false">Inactive only</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Clear Button */}
            <Grid item xs={12} sm={12} md={1.5}>
              <CustomButton
                variant="outlined"
                onClick={handleClearFilters}
                fullWidth
                sx={{
                  height: '2.75rem',
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: alpha('#11409B', 0.25),
                  color: '#11409B',
                  '&:hover': {
                    borderColor: '#11409B',
                    bgcolor: alpha('#11409B', 0.04),
                  },
                }}
              >
                Clear
              </CustomButton>
            </Grid>
          </Grid>
        </PanelCard>
      </motion.div>

      {/* Main Table */}
      <motion.div custom={6} initial="hidden" animate="visible" variants={fadeUp}>
        {loading ? (
          <PanelCard>
            <Stack alignItems="center" justifyContent="center" sx={{ py: 12 }}>
              <CircularProgress sx={{ color: '#11409B' }} />
              <Typography sx={{ mt: 2, color: '#64748b', fontWeight: 500 }}>
                Loading users with orders…
              </Typography>
            </Stack>
          </PanelCard>
        ) : usersData.length === 0 ? (
          <PanelCard>
            <Stack alignItems="center" justifyContent="center" sx={{ py: 10, px: 3 }}>
              <ShoppingBagOutlinedIcon sx={{ fontSize: 60, color: '#cbd5e1', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                No users found with orders
              </Typography>
              <Typography sx={{ color: '#64748b', mt: 1, textAlign: 'center', maxWidth: 460 }}>
                Try adjusting your search criteria, order status, or role filter.
              </Typography>
            </Stack>
          </PanelCard>
        ) : (
          <TableContainer component={PanelCard} sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 1050 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha('#11409B', 0.04) }}>
                  <TableCell sx={{ width: 44 }} />
                  <TableCell
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      color: '#11409B',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      py: 2,
                    }}
                  >
                    User
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      color: '#11409B',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      py: 2,
                    }}
                  >
                    Contact
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      color: '#11409B',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      py: 2,
                    }}
                  >
                    Role & Status
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      color: '#11409B',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      py: 2,
                    }}
                  >
                    Total Orders
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      color: '#16a34a',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      py: 2,
                    }}
                  >
                    Success
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      color: '#d97706',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      py: 2,
                    }}
                  >
                    Pending
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      color: '#dc2626',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      py: 2,
                    }}
                  >
                    Failed
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      color: '#11409B',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      py: 2,
                      pr: 3,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usersData.map((user) => {
                  const counts = user?.counts || {};
                  const total =
                    user?.totalOrdersCount ?? counts.total ?? (user?.orders?.length || 0);
                  const success =
                    user?.successOrdersCount ?? counts.success ?? (user?.successOrders?.length || 0);
                  const pending =
                    user?.pendingOrdersCount ?? counts.pending ?? (user?.pendingOrders?.length || 0);
                  const failed =
                    user?.failedOrdersCount ?? counts.failed ?? (user?.failedOrders?.length || 0);

                  const isExpanded = expandedUserIds.has(user.id);
                  const inlineOrders =
                    user.orders || [
                      ...(user.successOrders || []),
                      ...(user.pendingOrders || []),
                      ...(user.failedOrders || []),
                    ];

                  return (
                    <React.Fragment key={user.id || user.email || Math.random()}>
                      <TableRow
                        sx={{
                          transition: 'background 0.2s',
                          '&:hover': { bgcolor: alpha('#11409B', 0.03) },
                          '& td': {
                            borderBottom: `1px solid ${alpha('#11409B', 0.06)}`,
                            py: 1.75,
                          },
                        }}
                      >
                        {/* Expand button */}
                        <TableCell sx={{ pl: 2, pr: 0 }}>
                          <IconButton
                            size="small"
                            onClick={() => toggleExpandUser(user.id)}
                            disabled={inlineOrders.length === 0}
                            sx={{
                              color: isExpanded ? '#11409B' : '#94a3b8',
                              transition: 'transform 0.2s',
                            }}
                          >
                            {isExpanded ? (
                              <KeyboardArrowUpIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowDownIcon fontSize="small" />
                            )}
                          </IconButton>
                        </TableCell>

                        {/* User Identity */}
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Avatar
                              src={user.pictureUrl || user.profile}
                              sx={{
                                width: 42,
                                height: 42,
                                bgcolor: 'linear-gradient(135deg, #11409B, #2563eb)',
                                fontWeight: 700,
                                fontSize: '0.95rem',
                                border: '2px solid #fff',
                                boxShadow: '0 4px 12px rgba(17,64,155,0.15)',
                              }}
                            >
                              {(user.name?.[0] || user.username?.[0] || user.email?.[0] || 'U').toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.92rem' }}>
                                {user.name || user.username || '—'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                @{user.username || 'user'} • Registered{' '}
                                {user.createdAt ? dayjs(user.createdAt).format('DD MMM YYYY') : '—'}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        {/* Contact info */}
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                              <PhoneOutlinedIcon sx={{ fontSize: 14, color: '#11409B' }} />
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                                {formatPhone(user.phone)}
                              </Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                              <EmailOutlinedIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                {user.email || '—'}
                              </Typography>
                            </Stack>
                          </Stack>
                        </TableCell>

                        {/* Role & status */}
                        <TableCell>
                          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" gap={0.5}>
                            <Chip
                              label={user.role || 'USER'}
                              size="small"
                              sx={{
                                bgcolor: alpha('#7c3aed', 0.1),
                                color: '#6d28d9',
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                textTransform: 'uppercase',
                              }}
                            />
                            <Chip
                              label={user.isActive !== false ? 'Active' : 'Inactive'}
                              size="small"
                              sx={{
                                bgcolor:
                                  user.isActive !== false
                                    ? alpha('#16a34a', 0.12)
                                    : alpha('#64748b', 0.12),
                                color: user.isActive !== false ? '#16a34a' : '#64748b',
                                fontWeight: 700,
                                fontSize: '0.7rem',
                              }}
                            />
                            {user.isBlocked && (
                              <Chip
                                label="Blocked"
                                size="small"
                                sx={{
                                  bgcolor: alpha('#dc2626', 0.12),
                                  color: '#dc2626',
                                  fontWeight: 700,
                                  fontSize: '0.7rem',
                                }}
                              />
                            )}

                            {user.isFraud ? (
                              <Tooltip
                                title={
                                  <Box sx={{ p: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: '#fca5a5' }}>
                                      BLACKLISTED & LOCKED
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                      Reason: {user.fraudReason || 'Suspicious activity'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', color: '#cbd5e1' }}>
                                      By: {user.fraudFlaggedBy || 'SYSTEM'} •{' '}
                                      {user.fraudFlaggedAt ? dayjs(user.fraudFlaggedAt).format('DD MMM YYYY, HH:mm') : ''}
                                    </Typography>
                                  </Box>
                                }
                              >
                                <Chip
                                  icon={<GppBadIcon style={{ fontSize: 13, color: '#dc2626' }} />}
                                  label="BLACKLISTED"
                                  size="small"
                                  onClick={() => handleOpenFraudModal(user)}
                                  sx={{
                                    bgcolor: alpha('#dc2626', 0.15),
                                    color: '#dc2626',
                                    fontWeight: 800,
                                    fontSize: '0.7rem',
                                    border: '1px solid rgba(220, 38, 38, 0.3)',
                                    cursor: 'pointer',
                                  }}
                                />
                              </Tooltip>
                            ) : failed >= 3 ? (
                              <Tooltip title="Same-day failure threshold reached (≥3 failures). Auto-fraud detection applies.">
                                <Chip
                                  label="Failures ≥3"
                                  size="small"
                                  sx={{
                                    bgcolor: alpha('#dc2626', 0.08),
                                    color: '#dc2626',
                                    fontWeight: 700,
                                    fontSize: '0.68rem',
                                    border: '1px dashed rgba(220, 38, 38, 0.3)',
                                  }}
                                />
                              </Tooltip>
                            ) : null}
                          </Stack>
                        </TableCell>

                        {/* Total Orders */}
                        <TableCell align="center">
                          <Chip
                            label={total}
                            size="small"
                            sx={{
                              fontWeight: 800,
                              fontSize: '0.82rem',
                              bgcolor: alpha('#11409B', 0.08),
                              color: '#11409B',
                              px: 0.5,
                            }}
                          />
                        </TableCell>

                        {/* Success Orders */}
                        <TableCell align="center">
                          <Chip
                            label={success}
                            size="small"
                            icon={<CheckCircleOutlineIcon style={{ fontSize: 14, color: '#16a34a' }} />}
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              bgcolor: alpha('#16a34a', 0.12),
                              color: '#16a34a',
                              border: `1px solid ${alpha('#16a34a', 0.2)}`,
                            }}
                          />
                        </TableCell>

                        {/* Pending Orders */}
                        <TableCell align="center">
                          <Chip
                            label={pending}
                            size="small"
                            icon={<HourglassEmptyIcon style={{ fontSize: 14, color: '#d97706' }} />}
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              bgcolor: alpha('#d97706', 0.12),
                              color: '#d97706',
                              border: `1px solid ${alpha('#d97706', 0.2)}`,
                            }}
                          />
                        </TableCell>

                        {/* Failed Orders */}
                        <TableCell align="center">
                          <Chip
                            label={failed}
                            size="small"
                            icon={<ErrorOutlineIcon style={{ fontSize: 14, color: '#dc2626' }} />}
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              bgcolor: alpha('#dc2626', 0.12),
                              color: '#dc2626',
                              border: `1px solid ${alpha('#dc2626', 0.2)}`,
                            }}
                          />
                        </TableCell>

                        {/* Action: View Orders */}
                        <TableCell align="right" sx={{ pr: 3 }}>
                          <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                            <CustomButton
                              variant="outlined"
                              size="small"
                              onClick={() => handleOpenUserOrdersModal(user)}
                              startIcon={<VisibilityOutlinedIcon fontSize="small" />}
                              sx={{
                                borderRadius: '10px',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                py: 0.5,
                                px: 1.5,
                                borderColor: alpha('#11409B', 0.3),
                                color: '#11409B',
                                '&:hover': {
                                  bgcolor: alpha('#11409B', 0.06),
                                  borderColor: '#11409B',
                                },
                              }}
                            >
                              View orders
                            </CustomButton>

                            <Tooltip title={user.isFraud ? "Remove from blacklist & restore user" : "Blacklist user & lockout"}>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenFraudModal(user)}
                                sx={{
                                  color: user.isFraud ? '#16a34a' : '#dc2626',
                                  bgcolor: user.isFraud ? alpha('#16a34a', 0.08) : alpha('#dc2626', 0.08),
                                  border: `1px solid ${user.isFraud ? alpha('#16a34a', 0.25) : alpha('#dc2626', 0.25)}`,
                                  '&:hover': {
                                    bgcolor: user.isFraud ? alpha('#16a34a', 0.16) : alpha('#dc2626', 0.16),
                                  },
                                }}
                              >
                                {user.isFraud ? <LockOpenIcon sx={{ fontSize: 16 }} /> : <SecurityIcon sx={{ fontSize: 16 }} />}
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>

                      {/* Expandable row: quick inline order summary */}
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                py: 2,
                                px: 3,
                                my: 1.5,
                                borderRadius: '16px',
                                bgcolor: alpha('#11409B', 0.02),
                                border: `1px dashed ${alpha('#11409B', 0.18)}`,
                              }}
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                mb={1.5}
                              >
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#11409B' }}>
                                  Orders preview for {user.name || user.username} ({inlineOrders.length}{' '}
                                  orders)
                                </Typography>
                                <CustomButton
                                  size="small"
                                  onClick={() => handleOpenUserOrdersModal(user)}
                                  endIcon={<OpenInNewIcon fontSize="small" />}
                                  sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.78rem' }}
                                >
                                  Open full details
                                </CustomButton>
                              </Stack>

                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ bgcolor: alpha('#fff', 0.8) }}>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>
                                      Order ID
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>
                                      Transaction ID
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>
                                      Product
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>
                                      Status
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>
                                      Amount
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>
                                      Qty
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>
                                      Date
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>
                                      Invoice
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {inlineOrders.slice(0, 5).map((ord) => {
                                    const st = getStatusBadge(
                                      ord.orderStatus || ord.paymentStatus || ord.status || ord.resultType
                                    );
                                    const ordKey = ord.id || ord.orderId || ord._id || ord.externalOrderIdVoucher;
                                    const isDownloading = downloadingOrderId === ordKey;

                                    return (
                                      <TableRow key={ord.id || ord.orderId || Math.random()}>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                          {ord.id || ord.orderId || ord.externalOrderIdVoucher || '—'}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#64748b' }}>
                                          {ord.transactionId || ord.externalOrderIdPayment || '—'}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>
                                          {ord.productCode || ord.brandProductCode || '—'}
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            label={st.label}
                                            size="small"
                                            sx={{
                                              fontWeight: 700,
                                              fontSize: '0.68rem',
                                              bgcolor: st.bg,
                                              color: st.color,
                                              border: `1px solid ${st.border}`,
                                            }}
                                          />
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                          {formatAmount(ord.totalAmount ?? ord.amount ?? ord.denomination)}
                                        </TableCell>
                                        <TableCell>{ord.quantity ?? 1}</TableCell>
                                        <TableCell sx={{ color: '#64748b', fontSize: '0.78rem' }}>
                                          {ord.createdAt ? dayjs(ord.createdAt).format('DD MMM YYYY') : '—'}
                                        </TableCell>
                                        <TableCell align="right">
                                          <Tooltip title="Download invoice">
                                            <span>
                                              <CustomButton
                                                size="small"
                                                variant="outlined"
                                                disabled={isDownloading}
                                                onClick={() => handleDownloadInvoice(ord)}
                                                startIcon={
                                                  isDownloading ? (
                                                    <CircularProgress size={12} sx={{ color: '#11409B' }} />
                                                  ) : (
                                                    <ReceiptLongOutlinedIcon sx={{ fontSize: 14 }} />
                                                  )
                                                }
                                                sx={{
                                                  borderRadius: '8px',
                                                  textTransform: 'none',
                                                  fontWeight: 600,
                                                  fontSize: '0.72rem',
                                                  py: 0.25,
                                                  px: 1,
                                                  borderColor: alpha('#11409B', 0.25),
                                                  color: '#11409B',
                                                  '&:hover': {
                                                    bgcolor: alpha('#11409B', 0.06),
                                                    borderColor: '#11409B',
                                                  },
                                                }}
                                              >
                                                {isDownloading ? 'Downloading…' : 'Invoice'}
                                              </CustomButton>
                                            </span>
                                          </Tooltip>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </motion.div>

      {/* Pagination Bar */}
      {!loading && usersData.length > 0 && (
        <motion.div custom={7} initial="hidden" animate="visible" variants={fadeUp}>
          <PanelCard sx={{ mt: 3, p: 2 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
            >
              <Typography sx={{ color: '#64748b', fontWeight: 500, fontSize: '0.9rem' }}>
                Showing {page * size + 1}–
                {Math.min((page + 1) * size, totalElements)} of {totalElements} users
              </Typography>

              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Rows per page:
                </Typography>
                <Box
                  component="select"
                  value={size}
                  onChange={(e) => {
                    setSize(Number(e.target.value));
                    setPage(0);
                  }}
                  sx={{
                    border: `1px solid ${alpha('#11409B', 0.2)}`,
                    borderRadius: '10px',
                    px: 1.5,
                    py: 0.75,
                    fontSize: '0.875rem',
                    color: '#0f172a',
                    bgcolor: '#fff',
                    outline: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {[10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Box>

                <Stack direction="row" alignItems="center" spacing={1}>
                  <IconButton
                    onClick={() => setPage((p) => Math.max(p - 1, 0))}
                    disabled={page === 0}
                    sx={{
                      border: '1px solid',
                      borderColor: alpha('#11409B', 0.15),
                      borderRadius: '12px',
                      bgcolor: '#fff',
                      '&:hover': { bgcolor: alpha('#11409B', 0.06) },
                    }}
                  >
                    <KeyboardArrowLeftIcon />
                  </IconButton>
                  <Typography sx={{ fontWeight: 600, minWidth: 54, textAlign: 'center' }}>
                    {page + 1} / {totalPages || 1}
                  </Typography>
                  <IconButton
                    onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))}
                    disabled={page >= totalPages - 1}
                    sx={{
                      border: '1px solid',
                      borderColor: alpha('#11409B', 0.15),
                      borderRadius: '12px',
                      bgcolor: '#fff',
                      '&:hover': { bgcolor: alpha('#11409B', 0.06) },
                    }}
                  >
                    <KeyboardArrowRightIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </Stack>
          </PanelCard>
        </motion.div>
      )}

      {/* User Orders Detailed Modal / Dialog */}
      <Dialog
        open={userOrdersModalOpen}
        onClose={handleCloseUserOrdersModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            p: { xs: 1, sm: 2 },
            boxShadow: '0 25px 80px rgba(15, 23, 42, 0.25)',
          },
        }}
      >
        <DialogTitle sx={{ p: 2.5, pb: 1.5 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
            {selectedUser && (
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  src={selectedUser.pictureUrl || selectedUser.profile}
                  sx={{
                    width: 52,
                    height: 52,
                    bgcolor: 'linear-gradient(135deg, #11409B, #2563eb)',
                    fontWeight: 800,
                    fontSize: '1.25rem',
                  }}
                >
                  {(selectedUser.name?.[0] || selectedUser.username?.[0] || 'U').toUpperCase()}
                </Avatar>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                      {selectedUser.name || selectedUser.username || 'User Orders'}
                    </Typography>
                    <Chip
                      label={selectedUser.role || 'USER'}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        bgcolor: alpha('#7c3aed', 0.1),
                        color: '#6d28d9',
                      }}
                    />
                  </Stack>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    {selectedUser.email} • {formatPhone(selectedUser.phone)} • ID:{' '}
                    <span style={{ fontFamily: 'monospace' }}>{selectedUser.id}</span>
                  </Typography>
                </Box>
              </Stack>
            )}

            <IconButton onClick={handleCloseUserOrdersModal}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 2.5 }}>
          {selectedUser && (
            <Box>
              {/* User Orders summary pills */}
              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid item xs={6} sm={3}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: '14px',
                      bgcolor: alpha('#11409B', 0.05),
                      border: `1px solid ${alpha('#11409B', 0.1)}`,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                      Total Orders
                    </Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: '#11409B' }}>
                      {selectedUser?.totalOrdersCount ?? selectedUser?.counts?.total ?? 0}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: '14px',
                      bgcolor: alpha('#16a34a', 0.08),
                      border: `1px solid ${alpha('#16a34a', 0.18)}`,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#16a34a', fontWeight: 700 }}>
                      Success Orders
                    </Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: '#16a34a' }}>
                      {selectedUser?.successOrdersCount ?? selectedUser?.counts?.success ?? 0}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: '14px',
                      bgcolor: alpha('#d97706', 0.08),
                      border: `1px solid ${alpha('#d97706', 0.18)}`,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#d97706', fontWeight: 700 }}>
                      Pending Orders
                    </Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: '#d97706' }}>
                      {selectedUser?.pendingOrdersCount ?? selectedUser?.counts?.pending ?? 0}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: '14px',
                      bgcolor: alpha('#dc2626', 0.08),
                      border: `1px solid ${alpha('#dc2626', 0.18)}`,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#dc2626', fontWeight: 700 }}>
                      Failed Orders
                    </Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: '#dc2626' }}>
                      {selectedUser?.failedOrdersCount ?? selectedUser?.counts?.failed ?? 0}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Subtabs & search inside modal */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                spacing={2}
                sx={{ mb: 2 }}
              >
                <Box
                  sx={{
                    bgcolor: alpha('#11409B', 0.04),
                    borderRadius: '12px',
                    p: 0.5,
                  }}
                >
                  <Tabs
                    value={userOrdersSubTab}
                    onChange={(_, v) => setUserOrdersSubTab(v)}
                    sx={{
                      minHeight: 38,
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        minHeight: 38,
                        py: 0.75,
                        px: 2,
                        borderRadius: '10px',
                      },
                      '& .Mui-selected': {
                        bgcolor: '#fff',
                        color: '#11409B',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      },
                      '& .MuiTabs-indicator': { display: 'none' },
                    }}
                  >
                    <Tab label="All orders" />
                    <Tab label="Success" />
                    <Tab label="Pending" />
                    <Tab label="Failed" />
                  </Tabs>
                </Box>

                <TextField
                  size="small"
                  placeholder="Filter by Order ID or Product…"
                  value={userOrdersSearch}
                  onChange={(e) => setUserOrdersSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <SearchOutlinedIcon sx={{ color: '#94a3b8', fontSize: 18, mr: 0.5 }} />
                    ),
                  }}
                  sx={{
                    minWidth: { sm: 260 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                    },
                  }}
                />
              </Stack>

              {/* Orders Table */}
              {userOrdersLoading ? (
                <Stack alignItems="center" py={8}>
                  <CircularProgress size={36} sx={{ color: '#11409B' }} />
                  <Typography sx={{ mt: 2, color: '#64748b' }}>
                    Fetching user order history…
                  </Typography>
                </Stack>
              ) : activeUserOrdersList.length === 0 ? (
                <Stack alignItems="center" py={6}>
                  <ShoppingBagOutlinedIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                  <Typography sx={{ fontWeight: 600, color: '#475569' }}>
                    No orders matching this category
                  </Typography>
                </Stack>
              ) : (
                <TableContainer
                  sx={{
                    maxHeight: 420,
                    borderRadius: '16px',
                    border: `1px solid ${alpha('#11409B', 0.1)}`,
                  }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { bgcolor: alpha('#11409B', 0.05), fontWeight: 700 } }}>
                        <TableCell sx={{ fontSize: '0.72rem', color: '#11409B' }}>ORDER ID</TableCell>
                        <TableCell sx={{ fontSize: '0.72rem', color: '#11409B' }}>TRANSACTION ID</TableCell>
                        <TableCell sx={{ fontSize: '0.72rem', color: '#11409B' }}>PRODUCT</TableCell>
                        <TableCell sx={{ fontSize: '0.72rem', color: '#11409B' }}>STATUS</TableCell>
                        <TableCell sx={{ fontSize: '0.72rem', color: '#11409B' }}>AMOUNT</TableCell>
                        <TableCell sx={{ fontSize: '0.72rem', color: '#11409B' }}>QTY</TableCell>
                        <TableCell sx={{ fontSize: '0.72rem', color: '#11409B' }}>DATE</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.72rem', color: '#11409B' }}>INVOICE</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activeUserOrdersList.map((ord) => {
                        const st = getStatusBadge(
                          ord.orderStatus || ord.paymentStatus || ord.status || ord.resultType
                        );
                        const ordKey = ord.id || ord.orderId || ord._id || ord.externalOrderIdVoucher;
                        const isDownloading = downloadingOrderId === ordKey;

                        return (
                          <TableRow key={ord.id || ord.orderId || Math.random()} hover>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 600 }}>
                              {ord.id || ord.orderId || ord.externalOrderIdVoucher || '—'}
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#64748b' }}>
                              {ord.transactionId || ord.externalOrderIdPayment || '—'}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#0f172a' }}>
                              {ord.productCode || ord.brandProductCode || '—'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={st.label}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: '0.7rem',
                                  bgcolor: st.bg,
                                  color: st.color,
                                  border: `1px solid ${st.border}`,
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>
                              {formatAmount(ord.totalAmount ?? ord.amount ?? ord.denomination)}
                            </TableCell>
                            <TableCell>{ord.quantity ?? 1}</TableCell>
                            <TableCell sx={{ color: '#64748b', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                              {ord.createdAt
                                ? dayjs(ord.createdAt).format('DD MMM YYYY, HH:mm')
                                : '—'}
                            </TableCell>
                            <TableCell align="right">
                              <CustomButton
                                size="small"
                                variant="outlined"
                                disabled={isDownloading}
                                onClick={() => handleDownloadInvoice(ord)}
                                startIcon={
                                  isDownloading ? (
                                    <CircularProgress size={14} sx={{ color: '#11409B' }} />
                                  ) : (
                                    <ReceiptLongOutlinedIcon sx={{ fontSize: 15 }} />
                                  )
                                }
                                sx={{
                                  borderRadius: '10px',
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  py: 0.4,
                                  px: 1.25,
                                  whiteSpace: 'nowrap',
                                  borderColor: alpha('#11409B', 0.3),
                                  color: '#11409B',
                                  '&:hover': {
                                    bgcolor: alpha('#11409B', 0.06),
                                    borderColor: '#11409B',
                                  },
                                }}
                              >
                                {isDownloading ? 'Downloading…' : 'Download Invoice'}
                              </CustomButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Fraud Modal */}
      <FraudModal
        open={fraudModalOpen}
        onClose={() => {
          setFraudModalOpen(false);
          setFraudTargetUser(null);
        }}
        user={fraudTargetUser}
        onSuccess={() => {
          fetchUsersWithOrders();
        }}
      />
    </Box>
  );
};

export default UsersOrdersTab;
